import {get, isEmpty} from 'lodash';
import {readString} from 'react-papaparse';
import jschardet from 'jschardet';
import ExcelJS from 'exceljs';
import {isEmailValid, validatePhoneNumber, formatPhoneToE164} from "./functions";
import {i18n} from "./i18n/i18n";
import {getAllTranslationsForSubKeys} from "../contexts/I18nContext";

const SUPPORTED_FILE_TYPES = ['csv', 'xlsx', 'xls'];

/**
 * Builds a mapping from file headers to canonical field names
 * Runs once per file to map all headers at once
 * @param {object} params - Function parameters
 * @param {Array<string>} params.fileHeaders - Array of headers from the uploaded file
 * @param {string} params.i18nParentKey - Parent i18n key containing all field translations (e.g., 'importUsersModal.users')
 * @returns {Object} Map of { fileHeader: canonicalFieldName } for matched headers
 * @example
 * // For headers ['First Name', 'טלפון', 'Organizations'] returns:
 * // { 'First Name': 'firstName', 'טלפון': 'phone', 'Organizations': 'organizations' }
 */
const buildHeaderToFieldMap = ({fileHeaders, i18nParentKey}) => {
    const allFieldTranslations = getAllTranslationsForSubKeys({parentKey: i18nParentKey});
    const headerToFieldMap = {};

    fileHeaders.forEach(fileHeader => {
        if (!fileHeader) return;

        const trimmedHeader = fileHeader.trim();
        const lowerHeader = trimmedHeader.toLowerCase();

        // Check each field's translations to find a match
        for (const [fieldName, translations] of Object.entries(allFieldTranslations)) {
            // translations already includes both original and lowercase versions
            if (translations.includes(trimmedHeader) || translations.includes(lowerHeader)) {
                headerToFieldMap[fileHeader] = fieldName;
                break;
            }
        }
    });

    return headerToFieldMap;
};

/**
 * Helper function to read file as binary string (for encoding detection)
 * @param {File} file - The file to read
 * @returns {Promise<string>} Promise that resolves with binary string content
 */
const readAsBinaryString = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(get(e, 'target.result', ''))
        reader.onerror = (e) => reject(e)
        reader.readAsBinaryString(file)
    })
}

/**
 * Helper function to read file as ArrayBuffer (for parsing CSV/Excel)
 * @param {File} file - The file to read
 * @returns {Promise<ArrayBuffer>} Promise that resolves with ArrayBuffer content
 */
const readAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(get(e, 'target.result', ''))
        reader.onerror = (e) => reject(e)
        reader.readAsArrayBuffer(file)
    })
}

/**
 * Parse CSV file content to array of arrays format
 * @param {ArrayBuffer} fileBuffer - The file content as ArrayBuffer
 * @param {string} detectedEncoding - The detected text encoding
 * @returns {Object} Parsed data with structure: { data: [[row1], [row2], ...] }
 */
const parseCSV = ({fileBuffer, detectedEncoding}) => {
    const text = new TextDecoder(detectedEncoding).decode(fileBuffer)
    // Remove BOM (Byte Order Mark) if present - fixes Excel CSV export bug
    const textWithoutBOM = text.replace(/^\uFEFF/, '')
    const res = readString(textWithoutBOM)
    return res
}

/**
 * Parse Excel file content to array of arrays format
 * @param {ArrayBuffer} fileBuffer - The file content as ArrayBuffer
 * @returns {Promise<Object>} Parsed data with structure: { data: [[row1], [row2], ...] }
 */
const parseExcel = async ({fileBuffer}) => {
    const workbook = new ExcelJS.Workbook()
    
    try {
        await workbook.xlsx.load(fileBuffer)
        const worksheet = workbook.worksheets[0]
        
        if (!worksheet) {
            throw new Error("importUsersModal.errors.emptyExcelFile")
        }
        
        // Convert worksheet to array of arrays (same format as CSV)
        const jsonData = []
        worksheet.eachRow((row, rowNumber) => {
            const rowData = []
            row.eachCell({ includeEmpty: true }, (cell) => {
                let value = cell.value
                
                // Handle different cell types
                if (value === null || value === undefined) {
                    value = ''
                } else if (typeof value === 'object') {
                    if (value.result !== undefined) {
                        // Formula cell - use computed result
                        value = String(value.result || '')
                    } else if (value.richText) {
                        // Rich text - concatenate all text
                        value = value.richText.map(t => t.text).join('')
                    } else if (value instanceof Date) {
                        // Date object
                        value = value.toLocaleDateString('en-GB') // DD/MM/YYYY
                    } else {
                        value = String(value)
                    }
                } else {
                    value = String(value)
                }
                
                rowData.push(value)
            })
            jsonData.push(rowData)
        })
        
        return { data: jsonData }
        
    } catch (error) {
        console.error('Error parsing Excel file:', error)
        throw new Error("importUsersModal.errors.invalidExcelFile")
    }
}

/**
 * Mapping of canonical field names to their required columns names (used for error messages)
 * and additional configuration like validation/manipulation functions
 */
const FIELD_CONFIG = {
    firstName: {
        canonicalName: 'First Name',
        isMandatoryByDefault: true
    },
    lastName: {
        canonicalName: 'Last Name',
        isMandatoryByDefault: true
    },
    phone: {
        canonicalName: 'Phone',
        isMandatoryByDefault: true,
        manipulationFunction: formatPhoneToE164,
        validationFunction: validatePhoneNumber
    },
    email: {
        canonicalName: 'Email',
        validationFunction: isEmailValid
    },
    idNumber: {
        canonicalName: 'ID Number'
    },
    employeeId: {
        canonicalName: 'Employee ID'
    },
    organizations: {
        canonicalName: 'Organizations'
    }
};

/**
 * Import and parse user data from CSV or Excel file
 * Validates required columns, sanitizes data, and returns structured user objects
 *
 * @param {Object} params - The function parameters container object
 * @param {File} params.file - The file to import (CSV or Excel)
 * @param {Array<string>} [params.requiredColumns] - Required column canonical names
 * @param {string} params.companyCountryCode - Company's country code for phone validation (e.g., 'IL', 'US')
 * @returns {Promise<Object>} Parsed and validated user data { errors, data }
 * @throws {Error} If file type is invalid or required columns are missing
 */
export const importUsersFromFile = async ({file, requiredColumns = ['Phone', 'First Name', 'Last Name'], companyCountryCode}) => {
    // 1. Detect encoding (for CSV only)
    const binaryContent = await readAsBinaryString(file)
    const detectedEncoding = jschardet.detect(binaryContent).encoding

    // 2. Validate file extension
    const fileExtension = file.name.split('.').pop().toLowerCase()
    if (!SUPPORTED_FILE_TYPES.includes(fileExtension)) {
        throw new Error("importUsersModal.errors.unsupportedFileType")
    }

    // 3. Read file content as ArrayBuffer
    const fileBuffer = await readAsArrayBuffer(file)

    // 4. Parse based on file type
    let res

    if (fileExtension === 'csv') {
        res = parseCSV({fileBuffer, detectedEncoding})
    } else if (SUPPORTED_FILE_TYPES.includes(fileExtension)) {
        res = await parseExcel({fileBuffer})
    }

    const fileHeaders = res.data[0]

    // 5. Build header mapping ONCE for all columns
    const headerToFieldMap = buildHeaderToFieldMap({
        fileHeaders,
        i18nParentKey: 'importUsersModal.users'
    });

    // Create reverse map: fieldName -> fileHeader (for looking up values)
    const fieldToHeaderMap = {};
    Object.entries(headerToFieldMap).forEach(([header, field]) => {
        fieldToHeaderMap[field] = header;
    });

    // 6. Validate required columns exist in file
    requiredColumns.forEach(requiredCol => {
        // Find the field name for this canonical column name
        const fieldName = Object.entries(FIELD_CONFIG).find(
            ([, config]) => config.canonicalName === requiredCol
        )?.[0];

        if (!fieldName || !fieldToHeaderMap[fieldName]) {
            console.error(`importUsersFromFile - Missing required column: "${requiredCol}"`);
            throw new Error(i18n.translate(`importUsersModal.errors.missingColumn`, {context: {columnName: requiredCol}}));
        }
    });

    // 7. Build user data objects using the header mapping
    const data = {}
    res.data.slice(1).forEach((row, rowIndex) => {
        // Skip empty rows
        const allValuesEmpty = row.every(value => !value || (typeof value === 'string' && value.trim() === ''));
        if (allValuesEmpty) return;

        data[rowIndex] = {};

        // Map each cell to its canonical field name
        row.forEach((cellValue, colIndex) => {
            const fileHeader = fileHeaders[colIndex];
            const fieldName = headerToFieldMap[fileHeader];

            if (fieldName) {
                // Apply manipulation function if exists
                const config = FIELD_CONFIG[fieldName];
                let value = cellValue;

                if (config?.manipulationFunction && value) {
                    value = config.manipulationFunction(value, companyCountryCode);
                }

                data[rowIndex][fieldName] = value ? String(value).trim() : '';
            }
        });
    });

    // 8. Validate user data and collect errors
    const errors = {};

    Object.entries(data).forEach(([rowIndex, user]) => {
        Object.entries(FIELD_CONFIG).forEach(([fieldName, config]) => {
            const fieldValue = user[fieldName];
            const isFieldEmpty = !fieldValue || isEmpty(fieldValue);

            // Check if this field is required
            const isMandatory = requiredColumns.includes(config.canonicalName);
            const isMandatoryAndEmpty = isFieldEmpty && isMandatory;

            // Only run validation function if field has a value
            const failsValidation = !isFieldEmpty && config.validationFunction && !config.validationFunction(fieldValue, companyCountryCode);

            if (isMandatoryAndEmpty || failsValidation) {
                if (!errors[rowIndex]) {
                    errors[rowIndex] = {};
                }
                errors[rowIndex][fieldName] = true;
            }

            // Ensure field exists even if empty (for display purposes)
            if (user[fieldName] === undefined) {
                user[fieldName] = '';
            }
        });
    });

    return {errors, data}
}